import React from 'react';

import {cx} from '../../util';
import {Pre} from '../SyntaxHighlighter';
import Mermaid from './Mermaid';

/**
 * Legend for a Figure: a numbered list of the diagram's components. Rendered
 * by Figure (which pulls it out of its children); on its own it renders its
 * content directly.
 */
export const FigureLegend = ({children}) => (
  <div className="lesson-figure-legend text-sm text-gray-600 [&_ol]:my-2 [&_li]:my-1 [&_p]:my-1">
    {children}
  </div>
);
FigureLegend.lessonRole = 'figure-legend';

const isLegend = (node) => React.isValidElement(node)
  && node.type?.lessonRole === 'figure-legend';

/**
 * Wraps a diagram (Mermaid or image) with a numbered caption and an optional
 * component legend. `variant="hero"` renders larger, borderless, on a tinted
 * gradient with a centered caption and no figure number (numbering handled via
 * CSS counters on .lesson-prose).
 */
export const Figure = ({
  id,
  caption,
  variant,
  legendTitle = 'Components',
  children,
}) => {
  const kids = React.Children.toArray(children);
  const legend = kids.find(isLegend);
  const content = kids.filter((k) => !isLegend(k));
  const hero = variant === 'hero';

  return (
    <figure
      id={id}
      className={cx(
        'lesson-figure my-8 scroll-mt-24',
        hero
          ? 'lesson-figure-hero rounded-2xl bg-linear-to-b from-indigo-50/70 to-white p-6 md:p-8'
          : 'rounded-xl border border-gray-200 p-4 md:p-6',
      )}
    >
      <div
        className={cx(
          '[&_pre]:my-0 [&_.lesson-mermaid]:my-0 [&_p]:my-0 [&_img]:mx-auto [&_img]:my-0 [&_img]:rounded-lg',
          hero && 'md:px-4',
        )}
      >
        {content}
      </div>
      {legend && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            {legendTitle}
          </div>
          {legend}
        </div>
      )}
      {caption && (
        <figcaption
          className={cx(
            'mt-3 text-sm text-gray-500',
            hero ? 'text-center' : 'lesson-figure-caption',
          )}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

/**
 * <pre> override for lesson MDX: routes ```mermaid fences to the Mermaid
 * renderer, everything else to the shared code-block Pre.
 */
export const LessonPre = ({children, ...props}) => {
  const only = React.Children.toArray(children)[0];
  const className = only?.props?.className || '';
  if (/language-mermaid/.test(className)) {
    return <Mermaid code={String(only.props.children ?? '').trim()} />;
  }
  return <Pre {...props}>{children}</Pre>;
};

export default Figure;
